import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { 
  Users as UsersIcon, 
  Search, 
  Shield, 
  UserX, 
  UserCheck,
  Settings,
  MoreHorizontal,
  Crown,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { AdminUser, AdminPermission } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const Users: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'admin' | 'user'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', currentPage, searchTerm, filterType],
    queryFn: () => adminApi.getUsers({
      page: currentPage,
      limit: 20,
      search: searchTerm || undefined,
      isAdmin: filterType === 'all' ? undefined : filterType === 'admin',
    }),
    refetchInterval: 30000,
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: ({ userId, permissions }: { userId: string; permissions: string[] }) =>
      adminApi.updateUserPermissions(userId, permissions),
    onSuccess: () => {
      toast.success('Permissions updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowPermissionsModal(false);
      setSelectedUser(null);
    },
    onError: () => {
      toast.error('Failed to update permissions');
    },
  });

  const banUserMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      adminApi.banUser(userId, reason),
    onSuccess: () => {
      toast.success('User banned successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      toast.error('Failed to ban user');
    },
  });

  const unbanUserMutation = useMutation({
    mutationFn: (userId: string) => adminApi.unbanUser(userId),
    onSuccess: () => {
      toast.success('User unbanned successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      toast.error('Failed to unban user');
    },
  });

  const handlePermissions = (user: AdminUser) => {
    setSelectedUser(user);
    setShowPermissionsModal(true);
  };

  const handleBanUser = async (user: AdminUser) => {
    const reason = prompt('Enter reason for ban (optional):');
    if (reason === null) return; // User cancelled
    
    banUserMutation.mutate({ userId: user.id, reason: reason || undefined });
  };

  const handleUnbanUser = (user: AdminUser) => {
    if (!confirm(`Are you sure you want to unban ${user.displayName}?`)) return;
    unbanUserMutation.mutate(user.id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            User Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage user accounts, permissions, and access controls
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-blue-500 rounded-md p-3">
                <UsersIcon className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                <dd className="text-lg font-semibold text-gray-900">{usersData?.total || 0}</dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-purple-500 rounded-md p-3">
                <Crown className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Administrators</dt>
                <dd className="text-lg font-semibold text-gray-900">
                  {usersData?.items.filter(u => u.isAdmin).length || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-green-500 rounded-md p-3">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
                <dd className="text-lg font-semibold text-gray-900">
                  {usersData?.items.filter(u => !u.isBanned).length || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or Twitch ID..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="input pl-10"
              />
            </div>
          </div>
          
          {/* Filter */}
          <div className="sm:w-48">
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as 'all' | 'admin' | 'user');
                setCurrentPage(1);
              }}
              className="input"
            >
              <option value="all">All Users</option>
              <option value="admin">Administrators</option>
              <option value="user">Regular Users</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <AnimatePresence>
                {usersData?.items.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    onPermissions={handlePermissions}
                    onBan={handleBanUser}
                    onUnban={handleUnbanUser}
                  />
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-twitch-600" />
          </div>
        )}

        {!isLoading && (!usersData?.items || usersData.items.length === 0) && (
          <div className="text-center py-12">
            <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-500">
              {searchTerm || filterType !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'No users have been registered yet'
              }
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {usersData && usersData.total > 20 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(currentPage * 20, usersData.total)}
              </span>{' '}
              of <span className="font-medium">{usersData.total}</span> users
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!usersData.hasPrev}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!usersData.hasNext}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <PermissionsModal
          user={selectedUser}
          onClose={() => {
            setShowPermissionsModal(false);
            setSelectedUser(null);
          }}
          onUpdate={(permissions) => 
            updatePermissionsMutation.mutate({ 
              userId: selectedUser.id, 
              permissions 
            })
          }
          isLoading={updatePermissionsMutation.isPending}
        />
      )}
    </div>
  );
};

interface UserRowProps {
  user: AdminUser;
  onPermissions: (user: AdminUser) => void;
  onBan: (user: AdminUser) => void;
  onUnban: (user: AdminUser) => void;
}

const UserRow: React.FC<UserRowProps> = ({ user, onPermissions, onBan, onUnban }) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <motion.tr
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="hover:bg-gray-50"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <img
              className="h-10 w-10 rounded-full"
              src={user.profileImageUrl || `https://ui-avatars.com/api/?name=${user.displayName}&background=8b5cf6&color=fff`}
              alt={user.displayName}
            />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">{user.displayName}</div>
            <div className="text-sm text-gray-500">@{user.login}</div>
            {user.email && (
              <div className="text-xs text-gray-400">{user.email}</div>
            )}
          </div>
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {user.isAdmin ? (
            <>
              <Crown className="h-4 w-4 text-purple-500 mr-2" />
              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                Administrator
              </span>
            </>
          ) : (
            <>
              <UsersIcon className="h-4 w-4 text-gray-400 mr-2" />
              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                User
              </span>
            </>
          )}
        </div>
        {user.isAdmin && user.permissions && (
          <div className="text-xs text-gray-500 mt-1">
            {user.permissions.length} permissions
          </div>
        )}
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(user.lastLoginAt).toLocaleDateString()}
        <div className="text-xs text-gray-400">
          {new Date(user.lastLoginAt).toLocaleTimeString()}
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        {user.isBanned ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <UserX className="h-3 w-3 mr-1" />
            Banned
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </span>
        )}
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="text-gray-400 hover:text-gray-500 p-1 rounded-full hover:bg-gray-100"
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
          
          {showActions && (
            <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1">
                <button
                  onClick={() => {
                    onPermissions(user);
                    setShowActions(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Settings className="h-4 w-4 mr-3" />
                  Manage Permissions
                </button>
                
                {user.isBanned ? (
                  <button
                    onClick={() => {
                      onUnban(user);
                      setShowActions(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                  >
                    <UserCheck className="h-4 w-4 mr-3" />
                    Unban User
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      onBan(user);
                      setShowActions(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                  >
                    <UserX className="h-4 w-4 mr-3" />
                    Ban User
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </td>
    </motion.tr>
  );
};

interface PermissionsModalProps {
  user: AdminUser;
  onClose: () => void;
  onUpdate: (permissions: string[]) => void;
  isLoading: boolean;
}

const PermissionsModal: React.FC<PermissionsModalProps> = ({ user, onClose, onUpdate, isLoading }) => {
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(
    new Set(user.permissions || [])
  );

  const availablePermissions: { key: AdminPermission; label: string; description: string }[] = [
    { key: 'read:uploads', label: 'View Uploads', description: 'Can view upload logs and status' },
    { key: 'write:uploads', label: 'Manage Uploads', description: 'Can retry, cancel, and modify uploads' },
    { key: 'delete:uploads', label: 'Delete Uploads', description: 'Can permanently delete upload records' },
    { key: 'read:users', label: 'View Users', description: 'Can view user accounts and information' },
    { key: 'write:users', label: 'Manage Users', description: 'Can ban, unban, and modify user accounts' },
    { key: 'read:system', label: 'View System', description: 'Can view system metrics and logs' },
    { key: 'write:system', label: 'Manage System', description: 'Can clear cache, restart services, and manage system' },
  ];

  const handlePermissionToggle = (permission: string) => {
    const newPermissions = new Set(selectedPermissions);
    if (newPermissions.has(permission)) {
      newPermissions.delete(permission);
    } else {
      newPermissions.add(permission);
    }
    setSelectedPermissions(newPermissions);
  };

  const handleSave = () => {
    onUpdate(Array.from(selectedPermissions));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Manage Permissions
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Update permissions for <strong>{user.displayName}</strong>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-5">
              <div className="space-y-3">
                {availablePermissions.map((permission) => (
                  <div key={permission.key} className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id={permission.key}
                        type="checkbox"
                        checked={selectedPermissions.has(permission.key)}
                        onChange={() => handlePermissionToggle(permission.key)}
                        className="focus:ring-purple-500 h-4 w-4 text-purple-600 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor={permission.key} className="font-medium text-gray-700">
                        {permission.label}
                      </label>
                      <p className="text-gray-500">{permission.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading}
              className={clsx(
                'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm',
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
              )}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;